import { Component, OnInit } from '@angular/core';
import { InvoiceService } from 'src/app/services/core/quickbook/invoices/invoice.service';
import { InvoicePaymentService } from 'src/app/services/core/quickbook/invoice-payments/invoice-payment.service';
import { CustomersService } from 'src/app/services/core/quickbook/customers/customers.service';
import { NUMBER_OF_RECORDS, INVOICE_STATUS_STAT_CARDS, INVOICE_AMOUNT_STAT_CARDS, toInvoiceRow } from 'src/app/utils/common.utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, debounceTime, filter } from 'rxjs';
import * as moment from 'moment';
import { sweetAlert, sweetAlertWithHtml } from 'src/app/utils/swal';
import { getSkippedItemsHtml } from 'src/app/utils/skipped-alert.utils';
import { Router } from '@angular/router';

@UntilDestroy()

@Component({
  selector: 'app-invoices-list',
  templateUrl: './invoices-list.component.html',
  styleUrls: ['./invoices-list.component.css']
})
export class InvoicesListComponent implements OnInit {

  invoices: any = [];
  totalPages = 0;
  totalResults = 0;
  currentPage: number = 1;
  invoiceDetail: any;
  tripId: any = null;
  referencePicker: any = null;
  loadingId = null;
  selectedDropdownIndex: any;
  toggleActionButtons = false;
  search: any = '';
  private searchSubject = new Subject<string>();
  customers: any;
  selectedCustomerId = null;
  selectedStatus = '';
  invoiceDateRange: any = [moment().startOf('day').toDate(), moment().endOf('day').toDate()];
  stats: any = {};
  selectedInvoiceIds: string[] = [];
  paying = false;
  isPayModalOpen = false;
  isDropdownOpen = false;
  statusStatCards = INVOICE_STATUS_STAT_CARDS;
  amountStatCards = INVOICE_AMOUNT_STAT_CARDS;

  numberOfRecords = NUMBER_OF_RECORDS;

  // one trip opens its detail directly, several ids ask which trip first
  openReference(reference: any) {
    const ids = (reference?.id || '').split(',').map((id: string) => id.trim()).filter(Boolean);
    if (ids.length > 1) this.referencePicker = reference;
    else this.tripId = ids[0];
  }

  constructor(
    private invoiceService: InvoiceService,
    private invoicePaymentService: InvoicePaymentService,
    private customersService: CustomersService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initializeSearch();
    this.getCustomers();
    this.getInvoielist(this.currentPage);

  }

  getInvoielist(page: any) {
    const queryParams:any = {
      limit: this.numberOfRecords,
      page,
    }
    if (this.search) {
      queryParams.search = this.search.trim();
    }
    if (this.selectedCustomerId) {
      queryParams.customer = this.selectedCustomerId;
    }
    if (this.selectedStatus) {
      queryParams.status = this.selectedStatus;
    }
    const dates = this.invoiceDateQuery();
    if (dates.startDate) {
      queryParams.startDate = dates.startDate;
      queryParams.endDate = dates.endDate;
    }
    this.invoiceService.getInvoice(queryParams).pipe(untilDestroyed(this)).subscribe({
      next: (data: any) => {
        if (data) {
          const { data: rows = [], overview = {} } = (data?.[0] || {}) as any;
          this.invoices = (rows || []).map(toInvoiceRow);
          this.selectedInvoiceIds = [];
          this.isDropdownOpen = false;
          this.totalPages = overview.totalPages;
          this.totalResults = overview.totalResults;
          this.stats = overview;
        }
      },
      error: (err) => {
        sweetAlert('Error', err, 'error', 'Ok');
      }
    })
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.getInvoielist(page);
  }

  onCustomerFilterChange() {
    this.currentPage = 1;
    this.getInvoielist(this.currentPage);
  }

  onStatusFilterChange() {
    this.currentPage = 1;
    this.getInvoielist(this.currentPage);
  }

  onInvoiceDateChange(event: any) {
    if (!event?.[0] && !event?.[1]) {
      this.invoiceDateRange = null;
      this.currentPage = 1;
      this.getInvoielist(this.currentPage);
      return;
    }
    if (!event?.[0] || !event?.[1]) {
      return;
    }
    this.invoiceDateRange = event;
    this.currentPage = 1;
    this.getInvoielist(this.currentPage);
  }

  invoiceDateQuery() {
    if (!this.invoiceDateRange?.[0] || !this.invoiceDateRange?.[1]) {
      return {};
    }
    return {
      startDate: moment(this.invoiceDateRange[0]).format('YYYY-MM-DD'),
      endDate: moment(this.invoiceDateRange[1]).format('YYYY-MM-DD'),
    };
  }

  getCustomers() {
    this.customersService.getCustomers({ limit: 9999 }).pipe(untilDestroyed(this)).subscribe((data: any) => {
      if (data) {
        this.customers = (data.results || []).filter((customer: any) => customer.Id)
      }
    })
  }

  async onSyncInvoice(invoice: any, type: any) {
    const mPull = `Are you sure you want to overwrite invoice data with quickbooks data?`
    const mPush = 'Are you sure you want to overwrite the qucikbooks data with invoice data'
    const result = await sweetAlert('Alert', type === 'pull' ? mPull : mPush, 'warning', 'Yes', 'No');
    if (!result.value) return;
      
    const queryParams = {
      isSyncFrom: type === 'pull' ? true : false
    }
    if (type === 'pull') {
      invoice.pulling = true;
    } else {
      invoice.pushing = true;
    }
    
    this.invoiceService.syncInvoice(invoice.id, queryParams).subscribe(
      {
        next: (data: any) => {
          this.getInvoielist(this.currentPage);
          sweetAlert('Success', data?.message || 'Synced successfully', 'success', 'Ok');
        },
        error: (err) => {
          console.error(err);
          sweetAlert('Error', err, 'error', 'Ok');
          invoice.pulling = false;
          invoice.pushing = false;
        },
        complete: () => {          
          invoice.pulling = false;
          invoice.pushing = false;
        }
      })
  }

  showActionButtons(idx: any) {
    this.selectedDropdownIndex = idx;
    this.toggleActionButtons = true;
  }

  closeActionMenu(evt: any) {
    this.selectedDropdownIndex = -1;
    this.toggleActionButtons = false;
  }


  downloadInvoice(invoice: any) {
    this.invoiceService.downloadInvoice(invoice.id).subscribe({
      next: (data: any) => {
        console.log("data check",data);
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice_${invoice.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        sweetAlert('Error', err, 'error', 'Ok');
      }
    });
  }



  async deleteInvoice(invoice: any) {
    const result = await sweetAlert('Alert', 'Are you sure you want to delete this invoice?', 'warning', 'Yes', 'No');
    if (!result.value) return;

    this.invoiceService.deleteInvoice(invoice.id).pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.getInvoielist(this.currentPage);
        this.toggleActionButtons = false;
      },
      error: (err) => {
        sweetAlert('Error', err, 'error', 'Ok');
      }
    });
  }

  sendInvoice(invoice: any) {
    this.invoiceService.sendInvoice(invoice.id).pipe(untilDestroyed(this)).subscribe(() => {
      this.getInvoielist(this.currentPage);
      this.toggleActionButtons = false;
    }, err => {
      sweetAlert('Error', err, 'error', 'Ok');
    });
  }

  openDetailModal(invoice: any) {
    this.invoiceDetail = invoice;
  }


  initializeSearch() {
    this.searchSubject
      .pipe(
        debounceTime(500),
        filter((search: any) => !search || search.length >= 3),
        untilDestroyed(this)
      ).subscribe(() => {
        this.currentPage = 1;
        this.getInvoielist(this.currentPage);
      });
  }

  onSearchInput() {
    this.searchSubject.next(this.search);
  }

  openInvoiceFormWindow(invoiceId :any) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/quickbook/invoices/${invoiceId}/edit`])
    );
    window.open(url, "_blank");
  }

  toggleInvoice(id: string) {
    this.selectedInvoiceIds = this.selectedInvoiceIds.includes(id) ? this.selectedInvoiceIds.filter((x) => x !== id) : [...this.selectedInvoiceIds, id];
    this.isDropdownOpen = this.selectedInvoiceIds.length > 0;
  }

  toggleAllInvoices(checked: boolean) {
    this.selectedInvoiceIds = checked ? this.invoices.map((invoice: any) => invoice.id) : [];
    this.isDropdownOpen = checked;
  }

  toggleDropdown(event: MouseEvent) {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeFilterDropDown(evt: any) {
    this.isDropdownOpen = false;
  }

  // one batch = every selected invoice paid in full; the API skips the ones it cannot pay (paid, deposited, not synced, ...)
  onPaySelected(depositToAccountRef?: any) {
    this.paying = true;
    this.invoicePaymentService.saveInvoicePaymentBatch(this.selectedInvoiceIds, depositToAccountRef).pipe(untilDestroyed(this)).subscribe({
      next: (data: any) => {
        this.paying = false;
        this.isPayModalOpen = false;
        this.isDropdownOpen = false;
        this.getInvoielist(this.currentPage);
        if (data.skippedPayments?.length) {
          sweetAlertWithHtml('Alert', '', 'alert', 'OK', getSkippedItemsHtml(data.skippedPayments, `Batch ${data.batchId}: ${data.createdPayments.length} paid, ${data.skippedPayments.length} skipped.`, true, ['Customer', 'Invoice No.']));
        } else {
          sweetAlert('Success', `Batch ${data.batchId}: ${data.createdPayments.length} payment(s) created`, 'success', 'OK');
        }
      },
      error: (err) => {
        this.paying = false;
        this.isPayModalOpen = false;
        sweetAlert('Error', err, 'error', 'OK');
      }
    });
  }

  openReceivePayment(invoiceId: any) {
    this.router.navigateByUrl(`/quickbook/invoices/${invoiceId}/receive-payment`);
  }
}
